import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import logoMark from "../assets/logo-mark.png";
import { CaptchaStep, MfaCodeForm, MfaSetupForm, BackupCodes, ResetCodeForm, NewPasswordForm, passwordChecks } from "../components/AuthChallenge";

export default function AuthPage({ defaultMode = "login", hideBranding = false, onModeChange }) {
  const { login, register, verifyMfa, setupMfa, enableMfa, commitAuth } = useAuth();
  const [mode, setMode] = useState(defaultMode);
  const [stage, setStage] = useState("form"); // form | mfa | mfa_setup | backup
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // captcha
  const [captchaId, setCaptchaId] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaKey, setCaptchaKey] = useState(0);
  const [showCaptcha, setShowCaptcha] = useState(false);
  // mfa
  const [mfaToken, setMfaToken] = useState(null);
  const [setupToken, setSetupToken] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [authData, setAuthData] = useState(null);
  // password reset
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetToken, setResetToken] = useState(null);

  const resetCaptcha = () => { setCaptchaAnswer(""); setCaptchaKey((k) => k + 1); };
  const goMode = (next) => { setMode(next); setError(""); setShowCaptcha(false); resetCaptcha(); onModeChange?.(next); };

  const doForgot = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setStage("reset_code");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  const doResetVerify = async (code) => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password/verify", { email: forgotEmail, code });
      setResetToken(data.reset_token);
      setStage(data.mfa_required ? "reset_mfa" : "reset_new");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  const doResetMfa = async (code) => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password/mfa", { reset_token: resetToken, code });
      setResetToken(data.reset_token); setStage("reset_new");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  const doResetNew = async (pw) => {
    setError(""); setLoading(true);
    try {
      await api.post("/auth/reset-password", { reset_token: resetToken, new_password: pw });
      toast.success("Password updated. Please sign in with your new password.");
      setPassword(""); setStage("form"); goMode("login");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const submit = (e) => {
    e.preventDefault();
    setError("");
    setShowCaptcha(true); // captcha popup gates the actual login/register
  };

  const runAuth = async () => {
    setError(""); setLoading(true);
    try {
      if (mode === "register") {
        const r = await register(`${firstName.trim()} ${lastName.trim()}`.trim(), email, password, { id: captchaId, answer: captchaAnswer });
        setShowCaptcha(false);
        if (r.pending) setStage("pending");
        return;
      }
      const res = await login(email, password, { id: captchaId, answer: captchaAnswer });
      if (res.done) return; // signed in — routing takes over
      setShowCaptcha(false);
      if (res.mfa_required) { setMfaToken(res.mfa_token); setStage("mfa"); }
      else if (res.mfa_setup_required) {
        const s = await setupMfa(res.setup_token);
        setSetupToken(res.setup_token); setSetupData(s); setStage("mfa_setup");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const isCaptcha = detail && typeof detail === "object" && String(detail.code || "").includes("captcha");
      setError(formatApiError(detail) || err.message);
      resetCaptcha();
      if (!isCaptcha) setShowCaptcha(false); // non-captcha errors surface on the form
    } finally { setLoading(false); }
  };

  const doMfa = async (code) => {
    setError(""); setLoading(true);
    try { await verifyMfa(mfaToken, code); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const doSetup = async (code) => {
    setError(""); setLoading(true);
    try {
      const data = await enableMfa(setupToken, code);
      setAuthData(data); setStage("backup");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const wrap = (children) => (
    <div className={hideBranding ? "relative z-10" : "min-h-screen flex flex-col justify-center px-7 relative z-10"}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>{children}</motion.div>
    </div>
  );

  if (stage === "pending") return wrap(
    <div data-testid="signup-pending" className="text-center space-y-3">
      <div className="h-14 w-14 mx-auto rounded-2xl grid place-items-center gold-border" style={{ background: "var(--bg-elevated)" }}>
        <ShieldCheck size={26} className="gold-text" />
      </div>
      <h2 className="text-lg font-semibold" style={{ color: "var(--text-primary)" }}>You're on the list</h2>
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
        Thanks for signing up! The Family Vault is in a private beta right now. Your account is awaiting approval — you'll be able to sign in as soon as an admin lets you in.
      </p>
      <button data-testid="signup-pending-back" onClick={() => { setStage("form"); goMode("login"); }}
        className="mt-2 text-sm gold-text font-medium">Back to sign in</button>
    </div>
  );
  if (stage === "mfa") return wrap(<MfaCodeForm onSubmit={doMfa} error={error} loading={loading} />);
  if (showCaptcha) return wrap(
    <CaptchaStep
      onVerify={runAuth}
      onBack={() => { setShowCaptcha(false); setError(""); }}
      resetKey={captchaKey} answer={captchaAnswer} setAnswer={setCaptchaAnswer} onId={setCaptchaId}
      error={error} loading={loading}
      submitLabel={mode === "login" ? "Verify & sign in" : "Verify & create account"}
    />
  );
  if (stage === "forgot") return wrap(
    <form onSubmit={doForgot} data-testid="forgot-form" className="space-y-3">
      <div className="flex items-center gap-2 gold-text"><ShieldCheck size={18} /><span className="font-semibold text-sm">Reset your password</span></div>
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Enter your account email and we'll send you an 8-digit reset code.</p>
      <input data-testid="forgot-email-input" type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
        placeholder="Email address" autoComplete="username"
        className="w-full rounded-xl px-4 py-3.5 text-sm outline-none" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 }} />
      {error && <p className="text-xs" style={{ color: "#e06b70" }}>{error}</p>}
      <button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full rounded-xl py-3.5 font-semibold text-sm" style={{ background: "var(--accent)", color: "#fff", opacity: loading ? 0.6 : 1 }}>
        {loading ? "Sending…" : "Send reset code"}
      </button>
      <button type="button" onClick={() => { setStage("form"); setError(""); }} className="w-full text-center text-xs" style={{ color: "var(--text-secondary)" }} data-testid="forgot-back">Back to sign in</button>
    </form>
  );
  if (stage === "reset_code") return wrap(<ResetCodeForm email={forgotEmail} onSubmit={doResetVerify} onResend={() => doForgot({ preventDefault() {} })} error={error} loading={loading} />);
  if (stage === "reset_mfa") return wrap(<MfaCodeForm onSubmit={doResetMfa} error={error} loading={loading} />);
  if (stage === "reset_new") return wrap(<NewPasswordForm onSubmit={doResetNew} error={error} loading={loading} />);
  if (stage === "mfa_setup") return wrap(<MfaSetupForm setup={setupData} onSubmit={doSetup} error={error} loading={loading} forced />);
  if (stage === "backup") return wrap(<BackupCodes codes={authData?.backup_codes || []} onDone={() => commitAuth(authData)} />);

  return (
    <div className={hideBranding ? "relative z-10" : "min-h-screen flex flex-col justify-center px-7 relative z-10"}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        {!hideBranding && (
          <>
            <div className="h-16 w-16 rounded-2xl grid place-items-center gold-border mb-6"
              style={{ background: "var(--bg-elevated)", boxShadow: "0 0 24px rgba(124,131,255,0.2)" }}>
              <img src={logoMark} alt="The Family Vault" className="h-11 w-11 object-contain" />
            </div>
            <h1 className="font-serif-display text-5xl leading-none">The Family<br />Vault</h1>
            <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
              One private home for your family to connect, plan, and preserve a legacy.
            </p>
          </>
        )}

        <form onSubmit={submit} className={hideBranding ? "space-y-3" : "mt-9 space-y-3"}>
          {mode === "register" && (
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="auth-first-name-input" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name" required autoComplete="given-name"
                className="w-full rounded-xl px-4 py-3.5 text-sm outline-none focus:ring-2"
                style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", ringColor: "var(--accent)" }} />
              <input data-testid="auth-last-name-input" value={lastName} onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name" required autoComplete="family-name"
                className="w-full rounded-xl px-4 py-3.5 text-sm outline-none focus:ring-2"
                style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", ringColor: "var(--accent)" }} />
            </div>
          )}
          <input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address" required autoComplete="username"
            className="w-full rounded-xl px-4 py-3.5 text-sm outline-none focus:ring-2 focus:ring-[var(--accent)]"
            style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
          <input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" required minLength={6} autoComplete={mode === "login" ? "current-password" : "new-password"}
            className="w-full rounded-xl px-4 py-3.5 text-sm outline-none focus:ring-2 focus:ring-[var(--accent)]"
            style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />

          {mode === "register" && password.length > 0 && (() => {
            const c = passwordChecks(password);
            const item = (ok, l) => <span style={{ color: ok ? "var(--accent)" : "var(--text-muted)" }}>{ok ? "✓" : "○"} {l}</span>;
            return (
              <div className="grid grid-cols-2 gap-1 text-[11px] px-0.5" data-testid="register-password-checks">
                {item(c.len, "12+ characters")}{item(c.upper, "1 capital")}{item(c.num, "1 number")}{item(c.sym, "1 symbol")}
              </div>
            );
          })()}

          {mode === "login" && (
            <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotEmail(email); setError(""); setStage("forgot"); }}
              className="text-xs w-full text-right" style={{ color: "var(--text-secondary)" }}>
              Forgot password?
            </button>
          )}

          {error && <p className="text-xs" style={{ color: "#e06b70" }} data-testid="auth-error">{error}</p>}
          <motion.button whileTap={{ scale: 0.97 }} data-testid="auth-submit-btn" disabled={loading}
            className="w-full rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2"
            style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
            {loading && <Loader2 size={16} className="animate-spin" />}
            {mode === "login" ? "Enter the Vault" : "Create Account"}
          </motion.button>
        </form>

        {mode === "register" && (
          <p className="mt-4 text-xs text-center leading-relaxed" style={{ color: "var(--text-muted)" }} data-testid="auth-consent-line">
            By continuing you agree to our{" "}
            <Link to="/legal?tab=terms" target="_blank" rel="noopener noreferrer" className="gold-text font-medium underline underline-offset-2" data-testid="auth-terms-link">Terms of Use</Link>
            {" "}&amp;{" "}
            <Link to="/legal" target="_blank" rel="noopener noreferrer" className="gold-text font-medium underline underline-offset-2" data-testid="auth-privacy-link">Privacy Policy</Link>.
          </p>
        )}

        <button data-testid="auth-toggle-btn"
          onClick={() => goMode(mode === "login" ? "register" : "login")}
          className="mt-6 text-sm w-full text-center" style={{ color: "var(--text-secondary)" }}>
          {mode === "login" ? (<>New here? <span className="gold-text font-medium">Create an account</span></>)
                            : (<>Already have an account? <span className="gold-text font-medium">Sign in</span></>)}
        </button>
      </motion.div>
    </div>
  );
}
